import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/apiClient';
import { updateUserProfile } from '../services/userService';
import { getUserProfile } from '../services/authService';
import './FilingPurchase.css'; // We'll create this next

// Load Stripe outside of component render cycle
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Checkout form component
function CheckoutForm({ formId, formDetails, onSuccess, onError }) {
  const [errorMessage, setErrorMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({
    name: "",
    email: "",
    phone: "",
    address: {
      line1: "",
      city: "",
      state: "",
      postal_code: ""
    },
    card: ""
  });
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    },
  });

  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();

  // Get setup intent for saving the card
  useEffect(() => {
    const getSetupIntent = async () => {
      try {
        const setupIntent = await api.post('/payments/create-setup-intent');
        setClientSecret(setupIntent.data.client_secret);
      } catch (error) {
        console.error('Error getting setup intent:', error);
        setErrorMessage('Failed to initialize payment form. Please try again.');
      }
    };

    getSetupIntent();
  }, [formId]);

  // Fetch and pre-fill user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        setLoadingProfile(true);
        const userProfile = await getUserProfile();
        
        // Pre-fill form with user data from the database
        setBillingDetails(prevDetails => ({
          ...prevDetails,
          email: userProfile.email || currentUser.email || '',
          name: userProfile.first_name && userProfile.last_name 
            ? `${userProfile.first_name} ${userProfile.last_name}`.trim()
            : `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          phone: userProfile.phone || currentUser.phone || '',
          address: userProfile.address 
            ? (typeof userProfile.address === 'string' ? JSON.parse(userProfile.address) : userProfile.address)
            : prevDetails.address,
        }));
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fall back to currentUser data if profile fetch fails
        setBillingDetails(prevDetails => ({
          ...prevDetails,
          email: currentUser.email || '',
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          phone: currentUser.phone || '',
          address: currentUser.address 
            ? (typeof currentUser.address === 'string' ? JSON.parse(currentUser.address) : currentUser.address)
            : prevDetails.address,
        }));
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error for this field when user types
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFieldErrors(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: ""
        }
      }));
      
      // Handle nested address fields
      setBillingDetails(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ""
      }));
      
      setBillingDetails(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Reset all field errors
    setFieldErrors({
      name: "",
      email: "",
      phone: "",
      address: {
        line1: "",
        city: "",
        state: "",
        postal_code: ""
      },
      card: ""
    });
    
    setErrorMessage(null);

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    // Validate form
    let hasError = false;
    
    if (!billingDetails.name) {
      setFieldErrors(prev => ({ ...prev, name: "Please enter your name" }));
      hasError = true;
    }

    if (!billingDetails.email) {
      setFieldErrors(prev => ({ ...prev, email: "Please enter your email address" }));
      hasError = true;
    }
    
    if (!billingDetails.phone) {
      setFieldErrors(prev => ({ ...prev, phone: "Please enter your phone number" }));
      hasError = true;
    }
    
    if (!billingDetails.address.line1) {
      setFieldErrors(prev => ({ 
        ...prev, 
        address: { ...prev.address, line1: "Please enter your street address" } 
      }));
      hasError = true;
    }
    
    if (!billingDetails.address.city) {
      setFieldErrors(prev => ({ 
        ...prev, 
        address: { ...prev.address, city: "Please enter your city" } 
      }));
      hasError = true;
    }
    
    if (!billingDetails.address.state) {
      setFieldErrors(prev => ({ 
        ...prev, 
        address: { ...prev.address, state: "Please enter your state/province" } 
      }));
      hasError = true;
    }
    
    if (!billingDetails.address.postal_code) {
      setFieldErrors(prev => ({ 
        ...prev, 
        address: { ...prev.address, postal_code: "Please enter your ZIP/postal code" } 
      }));
      hasError = true;
    }
    
    if (hasError) {
      // Don't proceed if any field has an error
      return;
    }

    setProcessing(true);

    try {
      // Create payment method and confirm setup intent
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: billingDetails.name,
            email: billingDetails.email,
            phone: billingDetails.phone,
            address: billingDetails.address
          },
        },
      });

      if (result.error) {
        setFieldErrors(prev => ({
          ...prev,
          card: result.error.message
        }));
        setProcessing(false);
        return;
      }

      // Process payment for form filing
      const payment = await api.post(
        `/payments/form-payment`,
        {
          form_id: formId,
          payment_method_id: result.setupIntent.payment_method
        }
      );

      // Update user profile with address and phone data
      if (currentUser) {
        try {
          // Store address as JSON string
          const userProfileUpdate = {
            phone: billingDetails.phone,
            address: JSON.stringify(billingDetails.address)
          };
          
          await updateUserProfile(userProfileUpdate);
        } catch (profileError) {
          console.error('Error updating user profile:', profileError);
          // Continue with payment success even if profile update fails
        }
      }

      // Call the success handler with the payment
      onSuccess(payment.data);
    } catch (error) {
      console.error('Payment error:', error);
      setErrorMessage(error.response?.data?.detail || 'Failed to process payment. Please try again.');
      // Call the error handler with the error
      onError(error);
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      {loadingProfile && (
        <div className="loading-message">
          <p>Loading your profile information...</p>
        </div>
      )}
      
      {errorMessage && (
        <div className="error-message form-wide-error">
          <p>{errorMessage}</p>
        </div>
      )}
      
      <div className="form-section">
        <h3>Contact Information</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Name on Card</label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Jane Doe"
              required
              value={billingDetails.name}
              onChange={handleInputChange}
              className={fieldErrors.name ? "input-error" : ""}
            />
            {fieldErrors.name && <div className="field-error">{fieldErrors.name}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="jane.doe@example.com"
              required
              value={billingDetails.email}
              onChange={handleInputChange}
              disabled={currentUser && currentUser.email}
              className={`${currentUser && currentUser.email ? 'disabled-input' : ''} ${fieldErrors.email ? "input-error" : ""}`}
            />
            {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              placeholder="(555) 123-4567"
              required
              value={billingDetails.phone}
              onChange={handleInputChange}
              className={fieldErrors.phone ? "input-error" : ""}
            />
            {fieldErrors.phone && <div className="field-error">{fieldErrors.phone}</div>}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Billing Address</h3>
        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="address.line1">Address</label>
            <input
              id="address.line1"
              name="address.line1"
              type="text"
              placeholder="123 Main St"
              required
              value={billingDetails.address.line1}
              onChange={handleInputChange}
              className={fieldErrors.address?.line1 ? "input-error" : ""}
            />
            {fieldErrors.address?.line1 && <div className="field-error">{fieldErrors.address.line1}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="address.line2">Address Line 2 (Optional)</label>
            <input
              id="address.line2"
              name="address.line2"
              type="text"
              placeholder="Apartment, suite, etc."
              value={billingDetails.address.line2}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.city">City</label>
            <input
              id="address.city"
              name="address.city"
              type="text"
              placeholder="San Francisco"
              required
              value={billingDetails.address.city}
              onChange={handleInputChange}
              className={fieldErrors.address?.city ? "input-error" : ""}
            />
            {fieldErrors.address?.city && <div className="field-error">{fieldErrors.address.city}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="address.state">State</label>
            <input
              id="address.state"
              name="address.state"
              type="text"
              placeholder="CA"
              required
              value={billingDetails.address.state}
              onChange={handleInputChange}
              className={fieldErrors.address?.state ? "input-error" : ""}
            />
            {fieldErrors.address?.state && <div className="field-error">{fieldErrors.address.state}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.postal_code">ZIP Code</label>
            <input
              id="address.postal_code"
              name="address.postal_code"
              type="text"
              placeholder="94103"
              required
              value={billingDetails.address.postal_code}
              onChange={handleInputChange}
              className={fieldErrors.address?.postal_code ? "input-error" : ""}
            />
            {fieldErrors.address?.postal_code && <div className="field-error">{fieldErrors.address.postal_code}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="address.country">Country</label>
            <select
              id="address.country"
              name="address.country"
              value={billingDetails.address.country}
              onChange={handleInputChange}
              required
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Payment Details</h3>
        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="card-element">Credit or Debit Card</label>
            <div className={`card-element-container ${fieldErrors.card ? "card-error" : ""}`}>
              <CardElement id="card-element" options={cardElementOptions} />
            </div>
            {fieldErrors.card && <div className="field-error">{fieldErrors.card}</div>}
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="submit"
          disabled={!stripe || processing}
          className={`submit-button ${processing ? 'processing' : ''}`}
        >
          {processing ? 'Processing...' : `Pay ${formDetails?.estimated_fee || '$0.00'}`}
        </button>
      </div>
    </form>
  );
}

// Main component
function FilingPurchase() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [formDetails, setFormDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState(null);

  useEffect(() => {
    // Check if user is logged in and verified
    if (!currentUser) {
      const currentPath = location.pathname + location.search;
      navigate(`/signin?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }

    if (currentUser && !currentUser.isVerified) {
      navigate('/verify-email');
      return;
    }

    const params = new URLSearchParams(location.search);
    const formId = params.get('filing');
    
    if (!formId) {
      setError('No filing selected. Please choose a filing to purchase.');
      setLoading(false);
      return;
    }

    // Fetch form details
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/forms/${formId}`);
        setFormDetails(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching form details:', error);
        setError('Failed to load form details. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, location, navigate]);

  // Handle successful payment
  const handlePaymentSuccess = (paymentData) => {
    setSuccess(true);
    setPaymentResponse(paymentData);
    
    // Redirect to confirmation page after a brief delay
    setTimeout(() => {
      navigate('/filing-confirmation', { 
        state: { 
          paymentData, 
          formDetails 
        } 
      });
    }, 2000);
  };

  // Handle payment error
  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setError(error.response?.data?.detail || 'An error occurred during payment processing. Please try again.');
  };

  return (
    <div className="filing-purchase-page">
      <div className="container">
        <div className="filing-purchase-container">
          <div className="filing-purchase-header">
            <h1>Complete Your Filing Purchase</h1>
          </div>

          {loading ? (
            <div className="loading-container">
              <p>Loading filing details...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button 
                className="back-button"
                onClick={() => navigate(-1)}
              >
                Back to Filings
              </button>
            </div>
          ) : success ? (
            <div className="success-container">
              <div className="success-icon">
                <span className="check-mark">✓</span>
              </div>
              <h2>Payment Successful!</h2>
              <p>Thank you for your purchase. We're processing your filing and will send you a confirmation email shortly with next steps.</p>
              <p className="redirect-message">Redirecting to confirmation page...</p>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : (
            <div className="filing-purchase-content">
              <div className="filing-summary">
                <h2>{formDetails?.form_name}</h2>
                <p className="price">{formDetails?.estimated_fee}</p>
                <div className="summary-details">
                  <p>{formDetails?.short_description}</p>
                  <p className="note">* This is a one-time payment for filing this form. Upon completion, you will receive an email with further instructions and next steps.</p>
                </div>
              </div>

              <div className="payment-form-container">
                <Elements stripe={stripePromise}>
                  <CheckoutForm
                    formId={formDetails?.id}
                    formDetails={formDetails}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                </Elements>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FilingPurchase; 